import { useState, type ChangeEvent, type ReactElement } from 'react';

export interface NumberFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly step: number;
  /** Advisory bounds for the browser's spinner; typed values outside them are still accepted. */
  readonly min?: number;
  readonly max?: number;
  readonly unit?: string;
  readonly invalid?: boolean;
  readonly disabled?: boolean;
  readonly onCommit: (value: number) => void;
}

/**
 * Numeric text entry that keeps its own text while the player types and only
 * commits finished, finite numbers. Out-of-range numbers are committed on
 * purpose: the domain validator reports them, so the player sees why.
 */
export function NumberField({
  id,
  label,
  value,
  step,
  min,
  max,
  unit,
  invalid = false,
  disabled = false,
  onCommit,
}: NumberFieldProps): ReactElement {
  const [typed, setTyped] = useState<string | null>(null);
  const shown = typed ?? (Number.isFinite(value) ? String(value) : '');

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const text = event.target.value;
    setTyped(text);
    if (text.trim() === '') {
      return;
    }
    const parsed = Number(text);
    if (Number.isFinite(parsed)) {
      onCommit(parsed);
    }
  };

  return (
    <span className="ce-numfield">
      <label className="ce-visually-hidden" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="ce-numfield__input"
        type="number"
        inputMode="decimal"
        step={step}
        {...(min !== undefined ? { min } : {})}
        {...(max !== undefined ? { max } : {})}
        value={shown}
        disabled={disabled}
        aria-invalid={invalid}
        onChange={handleChange}
        onBlur={(): void => setTyped(null)}
      />
      {unit !== undefined ? (
        <span className="ce-numfield__unit" aria-hidden="true">
          {unit}
        </span>
      ) : null}
    </span>
  );
}
